import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatCard } from './components/StatCard';
import { SectionStatusChip } from './components/SectionStatusChip';
import { KpiTimeChart } from './components/KpiTimeChart';
import { SectionExportButton } from './components/SectionExportButton';
import { GlosarioHelpButton } from './components/GlosarioHelpButton';
import { fmtNum, fmtNumShort, fmtPeriodo, CHART_COLORS } from './components/kpiUtils';
import {
  UserPlus, Upload, Users, MapPin, Receipt, Building2, CalendarClock,
} from 'lucide-react';

const GEN_COLORS = { 'Masculino': '#3b82f6', 'Femenino': '#ec4899' };

const KpisAfiliacion = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const rootRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get('/kpis/afiliacion');
      setData(r.data);
    } catch (e) { toast.error('Error al cargar Afiliación'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const subirArchivo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/kpis/afiliacion/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.total} afiliados importados`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'No se pudo importar el archivo'); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const puede_cargar = data?.puede_cargar;
  const total = data?.total || 0;
  const porMes = data?.por_mes || [];
  const porGenero = data?.por_genero || [];
  const porProvincia = data?.por_provincia || [];
  const porCondicion = data?.por_condicion_fiscal || [];
  const porEntidad = data?.por_entidad || [];
  const recientes = data?.recientes || [];

  const ultimoMes = porMes.length ? porMes[porMes.length - 1] : null;
  const evoData = useMemo(() => porMes.map((m) => ({ label: fmtPeriodo(m.periodo, true), altas: m.altas })), [porMes]);
  const genData = useMemo(() => porGenero.map((g) => ({ label: g.label, total: g.total })), [porGenero]);

  if (loading) return <div className="p-8 text-slate-500" data-testid="kpis-afiliacion-loading">Cargando afiliación…</div>;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" ref={rootRef} data-testid="kpis-afiliacion">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-violet-500/10"><UserPlus className="h-6 w-6 text-violet-600" /></div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Afiliación</h1>
            <div className="mt-1.5"><SectionStatusChip seccionKey="kpis-afiliacion" /></div>
            <p className="text-sm text-slate-500">Padrón de afiliados: altas, demografía y distribución geográfica</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <GlosarioHelpButton seccion="afiliacion" testId="afiliacion-help-btn" />
          <SectionExportButton title="Afiliación" targetRef={rootRef} testId="afiliacion-export-btn" />
          {puede_cargar && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={importing} className="bg-violet-600 hover:bg-violet-700 active:scale-95 transition-transform" data-testid="afiliacion-subir-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importando…' : 'Subir reporte (XLSX)'}
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={subirArchivo} data-testid="afiliacion-file-input" />
            </>
          )}
        </div>
      </div>

      {total === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200 dark:border-slate-700" data-testid="afiliacion-empty">
          <UserPlus className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Todavía no hay un reporte de afiliación cargado.</p>
          <p className="text-sm text-slate-400 mt-1">{puede_cargar ? 'Subí el Excel "Reporte de Afiliación" para ver las estadísticas.' : 'Pedile a un administrador que cargue el reporte.'}</p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Afiliados totales" value={fmtNum(total)} icon={Users} accent="#7c3aed" testId="afiliacion-kpi-total" />
            <StatCard label={`Altas ${ultimoMes ? fmtPeriodo(ultimoMes.periodo) : ''}`} value={fmtNum(ultimoMes?.altas)} icon={CalendarClock} accent="#4f46e5" deltaLabel="último mes" testId="afiliacion-kpi-ultimo" />
            <StatCard label="Provincias" value={fmtNum(data?.provincias_total ?? porProvincia.length)} icon={MapPin} accent="#0ea5e9" testId="afiliacion-kpi-provincias" />
            <StatCard label="Entidades / sindicatos" value={fmtNum(data?.entidades_total ?? porEntidad.length)} icon={Building2} accent="#10b981" testId="afiliacion-kpi-entidades" />
          </div>

          {/* Evolución de altas */}
          <Card className="p-5 lg:p-6 mb-6" data-testid="afiliacion-evolucion">
            <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Evolución de altas por mes</h2>
            <KpiTimeChart data={evoData} series={[{ key: 'altas', name: 'Altas', color: '#7c3aed' }]} type="area"
              valueFormatter={fmtNum} axisFormatter={fmtNumShort} height={300} showLegend={false} testId="afiliacion-evo-chart" />
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="afiliacion-genero">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Por género</h2>
              <KpiTimeChart data={genData} xKey="label" series={[{ key: 'total', name: 'Afiliados', color: '#7c3aed' }]}
                type="barras" barLayout="vertical" cellColors={genData.map((g) => GEN_COLORS[g.label] || '#a78bfa')}
                height={Math.max(160, genData.length * 46)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="afiliacion-genero-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="afiliacion-condicion">
              <div className="flex items-center gap-2 mb-4"><Receipt className="h-4 w-4 text-slate-400" /><h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Por condición fiscal</h2></div>
              <KpiTimeChart data={porCondicion.map((c) => ({ label: c.condicion, total: c.total }))} xKey="label"
                series={[{ key: 'total', name: 'Afiliados', color: '#0ea5e9' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(160, porCondicion.length * 44)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="afiliacion-condicion-chart" />
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="p-5 lg:p-6" data-testid="afiliacion-provincia">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Top provincias</h2>
              <KpiTimeChart data={porProvincia.map((p) => ({ label: p.provincia, total: p.total }))} xKey="label"
                series={[{ key: 'total', name: 'Afiliados', color: '#10b981' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(200, porProvincia.length * 34)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="afiliacion-provincia-chart" />
            </Card>
            <Card className="p-5 lg:p-6" data-testid="afiliacion-entidad">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200 mb-4">Top entidades / sindicatos</h2>
              <KpiTimeChart data={porEntidad.map((e) => ({ label: e.entidad, total: e.total }))} xKey="label"
                series={[{ key: 'total', name: 'Afiliados', color: '#6366f1' }]} type="barras" barLayout="vertical" cellColors={CHART_COLORS}
                height={Math.max(200, porEntidad.length * 34)} valueFormatter={fmtNum} axisFormatter={fmtNumShort} showLegend={false} testId="afiliacion-entidad-chart" />
            </Card>
          </div>

          {/* Altas recientes */}
          <Card className="p-0 overflow-hidden" data-testid="afiliacion-recientes">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">Altas recientes</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                    <th className="text-left font-medium px-4 py-2.5">Fecha alta</th>
                    <th className="text-left font-medium px-4 py-2.5">Nombre</th>
                    <th className="text-left font-medium px-4 py-2.5">CUIT/CUIL</th>
                    <th className="text-left font-medium px-4 py-2.5">Género</th>
                    <th className="text-left font-medium px-4 py-2.5">Provincia</th>
                    <th className="text-left font-medium px-4 py-2.5">Entidad</th>
                    <th className="text-left font-medium px-4 py-2.5">Cond. fiscal</th>
                  </tr>
                </thead>
                <tbody>
                  {recientes.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-4 py-2.5 whitespace-nowrap text-slate-500">{r.fecha_alta || '—'}</td>
                      <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{r.nombre || '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{r.cuit || '—'}</td>
                      <td className="px-4 py-2.5">{r.genero || '—'}</td>
                      <td className="px-4 py-2.5">{r.provincia || '—'}</td>
                      <td className="px-4 py-2.5">{r.entidad || '—'}</td>
                      <td className="px-4 py-2.5">{r.condicion_fiscal || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <p className="text-xs text-slate-400 mt-4">Fuente: {data?.archivo || 'Reporte de Afiliación'} · cargado {data?.fecha_carga ? new Date(data.fecha_carga).toLocaleString('es-AR') : '—'}</p>
        </>
      )}
    </div>
  );
};

export default KpisAfiliacion;
